<template>
<div class="chapter">
<div class="inner_page">
<h3 class="h3a"><a id="ch03"></a><a id="page_7"></a><a class="nounderb" href="#/xhtml/content"><span class="txbdco">Date: August 14</span></a></h3>
<h3 class="h3"><span class="txbdco">Time: 3:00 p.m</span>.</h3>
<h3 class="h3"><span class="txbdco">Place: Green Room, the practice</span></h3>
<h3 class="h3"><span class="txbdco">Patient: Sara Matteus</span></h3>
<p class="paraspaceabove">“So, how’s your summer been?”</p>
<p class="indent">“Is it okay if I smoke?”</p>
<p class="indent">“Sure.”</p>
<p class="indent">Sara roots around in her camouflage-pattern cloth bag and pulls out a red
            pack of Prince cigarettes and a lighter. With chapped, trembling fingers she lights a
            cigarette and takes two deep puffs before she fixes her gaze on me. She inspects me
            awhile in silence before blowing out a cloud of smoke between us—a carcinogenic
            smoke screen—which for a moment conceals her heavily mascaraed eyes. There is
            something demonstrative about the gesture, something both playful and provocative that
            makes me decide not to release her gaze.</p>
<p class="indent">“Well?” Sara says slowly.</p>
<p class="indent">“Summer?”</p>
<p class="indent">“Oh, right. Summer. It was good. I worked at that bar in Gamla Stan,
            you know, by Järntorget.”</p>
<p class="indent">“I know. How have you been feeling?”</p>
<p class="indent">“Good, really good. Just great. No problems.”</p>
<p class="indent">Sara falls silent and looks at me with an inscrutable expression. She is
            twenty-five but doesn’t look a day over seventeen. Bleached-blond hair in various
            shades of white and butter-yellow curls down her slender shoulders, forming tangled
            ringlets along the way. Ringlets. She twists them with her fingers when she gets bored
            and sometimes slides them in and out of her mouth as she bites or chews on them. When
            she’s not chewing on her hair, she smokes. She seems to always have a cigarette
            ready in her chapped fingers.</p>
<p class="indent"><a id="page_8">“No anxiety?”</a></p><a id="page_8">
<p class="indent">“Nah. Well, maybe a <span class="txit">little</span>… at
            times. I mean, Midsummer and that kind of shit. Doesn’t everybody get anxiety
            then? Who doesn’t get anxious at Midsummer?”</p>
<p class="indent">She looks searchingly at me in silence. A smile plays at the edge of her
            mouth before she continues.</p>
<p class="indent">“You can bet your ass I get anxious.”</p>
<p class="indent">“And what did you do?”</p>
<p class="indent">“Nothing,” says Sara, looking at me through the cigarette
            smoke with an empty stare. She seems strangely indifferent in the face of the emotions
            of anxiety and estrangement triggered, she claims, by the Midsummer celebrations.</p>
<p class="indent">“You didn’t cut yourself?”</p>
<p class="indent">“Nah… Well, just a <span class="txit">little</span>. On my
            arms, that is. Just my arms. Had to, couldn’t put up with the Midsummer thing
            otherwise. But. Not much. I promised you that I wouldn’t cut myself anymore. I
            always keep my promises, really. Especially when I promise <span class="txit">you</span> something.”</p>
<p class="indent">I notice Sara hiding her forearms in a presumably unconscious gesture.</p>
<p class="indent">“How many times did you cut yourself?”</p>
<p class="indent">“What do you mean? Like, how many cuts?”</p>
<p class="indent">“No, <span class="txit">on how many occasions</span>?”</p>
<p class="indent">“Ahh, a few. A couple, maybe three times during the summer. I
            can’t remember…”</p>
<p class="indent">Sara’s voice trails off and she puts out the cigarette in the blue
            flower vase that I had put on the coffee table in an attempt to make the room more
            inviting. I must be the only psychologist in Sweden who allows a patient to smoke, but
            Sara gets so restless otherwise, it’s almost impossible to carry on a conversation
            with her.</p>
<p class="indent">“Sara, this is important. I want you to return to those occasions
            when you cut yourself. Try to remember what happened, what it was that triggered the
            feelings that made you do it.”</p>
<p class="indent">“Uh…”</p>
<p class="indent">“Start with the first time. Take your time. When was it? Start by
            telling me when it happened.”</p>
</a><p class="indent"><a id="page_8"></a><a id="page_9">“Must have been Midsummer Eve. At the Midsummer
            celebration, that is. I already said that!”</a></p><a id="page_9">
<p class="indent">“What did you do that night? I mean, beforehand?”</p>
<p class="indent">“I went to see my mom. It was just her and me. She had made some
            food and stuff. And she bought wine.”</p>
<p class="indent">“So you weren’t at a Midsummer party?”</p>
<p class="indent">“Nah, that was more like a, what’s it called… a
            metaphor. A metaphor for how fucked-up Midsummer is. Everyone’s so happy. You have
            to socialize with your family and be happy. It’s somehow so…
            forced.”</p>
<p class="indent">“So the two of you weren’t happy?”</p>
<p class="indent">Sara sits quietly for a long time without speaking and for once holds her
            hands calmly in her lap while she thinks. The only sound in the room is the humming of
            the video camera as it records our conversation. She sighs deeply, and when she starts
            talking again, I can sense her irritation despite the calm, expectant tone of voice.</p>
<p class="indent">“Nah, but I’m sure you get that. I really don’t
            understand where this is supposed to lead. I’ve talked about my old lady at least
            a <span class="txit">thousand</span> times. You <span class="txit">know</span>
            she’s a drunk. <span class="txit">Hello</span>, do I have to write it down for
            you? It was like it always was. Everything was going to be so nice… and
            then… she just drank, and then she started bawling. You know that’s how she
            gets when she drinks. Sad and… like… She’s, like, sorry. Like she
            regrets everything. Like I should sit there and forgive her because she hasn’t
            been a good mother. Do you think I should forgive her?”</p>
<p class="indent">“What do you think?”</p>
<p class="indent">“Nah, I don’t think so. I think it’s unforgivable, what
            she did to me.”</p>
<p class="indent">“So what did you do?”</p>
<p class="indent">Sara shrugs, and I can tell by her posture that she no longer wants to
            talk about either her mom or herself. Her voice has become shrill, and pink patches are
            spreading on her neck like spilled wine on a tablecloth.</p>
<p class="indent">“I split. I can’t stand it when she’s
            bawling.”</p>
<p class="indent">“And then?”</p>
<p class="indent">Sara squirms and lights yet another cigarette.</p>
<p class="indent">“Home, I went home.”</p>
<p class="indent">“And?”</p>
</a><p class="indent"><a id="page_9"></a><a id="page_10">“But you <span class="txit">KNOW</span> what
            happened then. It’s the old bag’s fault. It’s like I
            can’t… can’t breathe when I’ve been there.”</a></p><a id="page_10">
<p class="indent">Now Sara is getting angry. That’s good. I’ll try to hold on to
            that feeling. When Sara is angry, the truth often comes out. The protective shield of
            self-manipulation disappears, replaced by the raw honesty of someone who doesn’t
            have much to lose, who doesn’t care what you think of her.</p>
<p class="indent">“You cut yourself?”</p>
<p class="indent">“Damn straight I <span class="txit">cut</span> myself.”</p>
<p class="indent">“Tell me more.” I say.</p>
<p class="indent">“But, <span class="txit">seriously</span>, you <span class="txit">know</span> what happened.”</p>
<p class="indent">“This is important, Sara.”</p>
<p class="indent">“I cut myself on the arm. Satisfied now?”</p>
<p class="indent">“Sara… listen to me! What you’re describing, what
            you’re feeling, it’s completely understandable. It’s Midsummer, you
            see your mom, she is drunk and asks you for forgiveness, this stirs up a lot of
            emotions. Can you see that?”</p>
<p class="indent">Sara looks down at her fingers, closely studying every fingernail. She
            nods, as if to confirm that she too thinks that maybe her emotions and reactions are
            understandable.</p>
<p class="indent">“The problem is that when you start feeling anxious, you want to cut
            yourself, which is not a good solution, especially not in the long run.”</p>
<p class="indent">Sara nods again. She knows that the cutting, the drinking, the impulsive
            sexual relationships provide relief only for the moment, and that the self-loathing and
            pain come back with redoubled force. Her desperate attempts to try to keep the anxiety
            at bay only seem to increase it.</p>
<p class="indent">“Did you try to do what we talked about before? You know, trying to
            put up with the anxiety. In itself, anxiety is never dangerous. It just feels that way.
            That’s what you have to work with, putting up with that feeling. Just for a while,
            because then it passes.”</p>
<p class="indent">“I know.”</p>
<p class="indent">“And the other times?”</p>
<p class="indent">“What other times?”</p>
<p class="indent">“That you cut yourself.”</p>
</a><p class="indent"><a id="page_10"></a><a id="page_11">She sighs and looks pointedly out the window. Fury has
            partly been replaced by fatigue in her voice.</a></p><a id="page_11">
<p class="indent">“Oh, yeah. One time I was drunk, so that doesn’t really count.
            I’m not really myself then. It was at a party in Haninge, with a guy from
            work.”</p>
<p class="indent">“Did anything in particular happen at the party that triggered those
            emotions?”</p>
<p class="indent">Sara shrugs and drops yet another cigarette butt in the vase with my by
            now nicotine-poisoned flowers.</p>
<p class="indent">“Try. Sara, this is important. And you have to help. I know
            it’s hard.”</p>
<p class="indent">“There was a guy there…”</p>
<p class="indent">“And?”</p>
<p class="indent">“And, he was a little like Göran.”</p>
<p class="indent">“Your foster dad?”</p>
<p class="indent">“Yeah.” Sara nods. “He touched me like Göran.
            Suddenly… you know I don’t like thinking about that stuff, and when he
            started groping me, grabbing me with his icky hands, it all just came back. I pushed him
            hard, right into a table. He was really loaded, so he fell down and cut his
            eyebrow.”</p>
<p class="indent">“What happened then?”</p>
<p class="indent">“Well, he got really mad. Started yelling and chasing me
            around.”</p>
<p class="indent">Sara suddenly looks exhausted and strangely small in her chair.</p>
<p class="indent">“Listen, it wasn’t really as bad as it sounds. He was loaded,
            did I already say that? He couldn’t get ahold of me. I went home.”</p>
<p class="indent">“And?”</p>
<p class="indent">“And then I did it, <span class="txit">okay</span>? Can we talk
            about <span class="txit">something else</span> now?”</p>
<p class="indent">“Try to describe how you felt just before you cut
            yourself.”</p>
<p class="indent">“What I <span class="txit">felt</span> then? HEL-LO, you KNOW how
            I felt. Like I was about to fall <span class="txit">apart</span>. I thought about that
            disgusting guy and about his icky groping and about Göran, and then it felt like I
            was going to fall apart, or that I couldn’t get any air. And then I did it, and
            then it felt better. I felt, like, cleaner. And calm. I could sleep. Okay? Can we talk
            about something else now? I’ve got to go soon anyway. I have an interview for an
            internship. Can we talk next time instead?”</p>
<p class="indent">“Until then I want you to do the homework we talked about,
            Sara.”</p>
</a><p class="indent"><a id="page_11"></a><a id="page_12">“Sure. So I can leave now?”</a></p><a id="page_12">
<p class="indent">“Go ahead. See you next week.”</p>
<p class="centera">•  •  •</p>
<p class="paraspaceabovebm">I turn off the video camera and sink back in my chair. As always
            after my sessions with Sara, I feel drained. It’s not just due to all the awful
            things she tells me, it’s also because I have to be on my guard the whole
            time—being Sara’s therapist is like walking a tightrope.</p>
<p class="indent">Her background is unfortunately not all that unusual. She grew up in a
            seemingly normal middle-class home in Vällingby, the youngest of three siblings.
            The only thing that was abnormal in her family situation was that her mother had
            problems with alcohol, but she could still function well socially. Sara always says that
            she even benefited from her mother’s drinking at times, when she was younger. Her
            mother kept silent at the parent-teacher meetings, for example, aware that she would
            expose herself as an alcoholic the moment she opened her mouth. She was always wasted
            when Sara came home, never questioned where she had been, why she came home in the
            middle of the night, or now and then where she got her new clothes—clothes her
            parents hadn’t bought for her.</p>
<p class="indent">From an early age, Sara had difficulties concentrating and problems in
            school. In third grade she set fire to the curtains in the gym with a lighter she had
            swiped from the PE teacher (who would always sneak a smoke in the locker room while the
            kids jogged around the schoolyard, lap after lap in the fall rain). In middle school she
            rode in a police car for the first time after she was caught shoplifting at the Konsum
            supermarket. She started seeing older guys and paired up with Steffe, who was eighteen
            when she was thirteen. She got pregnant and had an abortion.</p>
</a><p class="indent"><a id="page_12">It was around then that her parents realized they had completely lost
            control and they went to social services to get help. An investigation by the local
            office resulted in Sara’s being assigned a contact person and forced to submit
            regular urine samples. Such measures are usually pretty toothless, and they were in this
            case, too. Sara’s contact person gave up </a><a id="page_13">the assignment after
            Sara called her obscene names and spit on her desk. The social worker claimed that she
            felt threatened by Sara, but perhaps the truth was that she got tired of how difficult
            and demanding Sara was, and that not even she could cope with her.</a></p><a id="page_13">
<p class="indent">Aggressive? Absolutely. But I have never seen Sara hurt anyone other than
            herself. She has what might best be described as an unfailing, almost paranormal
            capacity to consistently choose the option most detrimental to her, always following the
            path that causes her the maximum amount of pain. She seems to have a kind of built-in,
            indestructible Via Dolorosa compass in her skull.</p>
<p class="indent">After she offended the social worker, Sara was placed in a foster home.
            When she was fifteen, she was raped repeatedly by her foster father, which led her to do
            the only—from her viewpoint—logical thing: try to run away. She almost
            succeeded several times but was always caught and returned to the foster home by the
            overzealous local police force. That was when the truly destructive behavior
            started—the self-injuring and sexual acting out.</p>
<p class="indent">When Sara was eighteen, she received a real psychiatric diagnosis for the
            first time: borderline personality disorder. As usual, the fact that psychiatry was
            finally able to put a label on what was wrong with her did not help. She got steadily
            worse. Shortly thereafter she was treated at a psychiatric clinic for two months for a
            presumably drug-induced psychosis-like condition. Sara herself usually refers to the
            psych clinic as “hell,” and I assume that in her descent there she more or
            less gave up any ambition of having a normal life, a “Svensson” life, as she
            always called it. In Sara’s case, the stint at the psychiatric institution was
            followed by increasingly intense drug abuse, and six months after she was discharged
            from the clinic, she was committed to a facility in Norrtälje for treatment with
            special focus on her drug problems, which at that point primarily consisted of abuse of
            amphetamines and synthetic hallucinogens.</p>
<p class="indent">Then something happened. It’s not clear what. Not even Sara can
            explain it, other than that she decided to live. So as not to die.</p>
</a><p class="indent"><a id="page_13">And today? Drug-free for two years and living in her own apartment </a><a id="page_14">in Midsommarkransen. Unemployed. Single. With many friends and even
            more boyfriends.</a></p><a id="page_14">
<p class="indent">Sara is truly a veteran within psychiatry. She has been analyzed from
            every which way: by psychiatric outpatient clinics and in institutional care. She has
            met more social workers, caseworkers, psychologists, and psychiatrists than I have had
            patients. This creates an obligation. Sometimes it feels as if she is evaluating me and
            my analysis, categorizing me and sorting me into a mental ranking of shrinks. She makes
            comments that undoubtedly are derived from conversations with my predecessors: “I
            see, but have you taken into account the increasing sibling rivalry that was the result
            of my parents’ early separation?” or “I realize this sounds really
            Oedipal, but sometimes I actually thought Göran loved me in <span class="txit">that</span> way.”</p>
<p class="indent">I think of Sara’s thin, scarred legs and arms. They resemble a
            railroad yard where the tracks sometimes cross each other, sometimes run parallel. Girls
            who cut themselves to relieve their anxiety are often called “cutters.”</p>
<p class="indent">But obviously Sara is much more than a psychiatric diagnosis. She is
            intelligent, an expert at manipulation, and, actually, pretty entertaining when she is
            in the right mood. Now she needs to be rehabilitated once again. To the kind of <span class="txit">normal</span> life she has never had and never expected to have.</p>
<p class="indent">I put my hand on her file—as thick as a Bible—complete with
            investigations from social service agencies, case records from the psychiatric
            outpatient clinic and institutional care. I browse through the documents absentmindedly.
            My gaze stops at the notes from St. Göran’s Hospital, from the time when Sara
            was admitted to the psych ward.</p>
<p class="blocktop"> </p>
<p class="block">          <span class="txit">Patient:</span> Sara Matteus, ID no. 82 11 23–0424</p>
<p class="block">        <span class="txit">Reason for admission:</span> Pat. comes to psych. ER 6:37 p.m. via
            Norrmalm police after having been taken into custody for shoplifting in the Twilfit
            store, Gallerian. As pat. acted confused and aggressive, police brought pat. to psych.
            ER.</p>
</a><p class="block"><a id="page_14">        <span class="txit">Current:</span> Pat. is an 18-year-old woman with drug problems and
            anxiety disorder UNS. She has previously been in contact both </a><a id="page_15">with
            CAP and psychiatric outpatient (Vällingby outpatient clinic). Presently pat. has no
            psychiatric contact and is not on medication. Pat. states that she does not feel well
            and wants help. She is oriented for brief periods and then complains of severe anxiety
            and also says that she has taken drugs but cannot remember what. Otherwise aggressive
            and expresses paranoid compulsive ideas that she is being persecuted by social services
            and police. Pat. shows signs of self-injuring behavior (scars and wounds on forearms and
            inside of thighs).</a></p><a id="page_15">
<p class="blocktop"> </p>
<p class="noindent">I sigh and let go of the heavy file, causing the documents to fall on
            the floor with a dull thud. I’ve had my dose of Sara Matteus for the day.
            It’s time to air out the office and see Ilja, the Russian mother of a small child
            who met her Swedish husband on the Internet. The woman who is doing so well in Sweden
            and already speaks fluent Swedish. The woman who is so capable and well-adjusted, and
            works as an OR nurse at Sophiahemmet, but who suffers from an irrepressible compulsion
            to hide all the knives and scissors in the family’s garden shed out of fear of
            hurting her baby with a sharp object.</p>
<p class="imagecm1"><img alt="logo" height="45" src="images/common.jpg" width="60"/></p>
</a>
</div>
</div>
</template>
<script>

export default {
  name: 'chapter',
  data() {
    return {}
    },
      components: {
    }
  }
</script>



// WEBPACK FOOTER //
// ch03.vue?7e1b8a48